import { useEffect, useState, useCallback } from "react";
import { X, RotateCcw, History, Loader2, Undo2, Trash2, CalendarClock } from "lucide-react";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";

const ACTION_LABEL = {
  before_update: "Kursdaten geändert", before_delete: "Kurs gelöscht",
  before_session_add: "Termin hinzugefügt", before_session_change: "Termin geändert",
  before_session_delete: "Termin gelöscht", before_generate: "Termine generiert",
  before_undo: "Vor Rückgängig", before_restore: "Vor Wiederherstellung",
};

export const CourseHistoryModal = ({ course, onClose, onRestored }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/courses/${course.id}/history`).then((r) => setItems(r.data.items)).catch((e) => toast.error(apiError(e)));
  }, [course.id]);
  useEffect(() => { load(); }, [load]);

  const undo = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/courses/${course.id}/undo`);
      toast.success(`Rückgängig gemacht (${r.data.label || "letzte Änderung"})`);
      onRestored && onRestored();
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const restore = async (h) => {
    if (!window.confirm(`Diese Version wiederherstellen?\n\n${ACTION_LABEL[h.action] || h.label} · ${fmtDateTime(h.created_at)}\n\nDer aktuelle Stand wird vorher gesichert (ebenfalls rückgängig machbar).`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/course-history/${h.id}/restore`);
      toast.success("Version wiederhergestellt");
      onRestored && onRestored();
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-[#0F0F0F] border border-white/15 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="course-history-modal">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 sticky top-0 bg-[#0F0F0F]">
          <div className="flex items-center gap-2"><History className="text-[#ff0033]" size={18} /><span className="font-heading uppercase tracking-tight text-white">Verlauf · {course.name}</span></div>
          <button onClick={onClose} data-testid="course-history-close" className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5">
          <button onClick={undo} disabled={busy || !items || items.length === 0} data-testid="course-undo-btn"
            className="w-full flex items-center justify-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider py-3 mb-5 disabled:opacity-40 hover:bg-[#cc0029] transition-colors">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Undo2 size={16} />} Letzte Änderung rückgängig
          </button>

          {items === null ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#ff0033]" /></div>
          ) : items.length === 0 ? (
            <p className="text-neutral-500 text-sm text-center py-6">Noch keine gespeicherten Versionen. Sobald du den Kurs bearbeitest, erscheinen hier ältere Stände.</p>
          ) : (
            <div className="space-y-2">
              <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Frühere Stände (jeweils vor der Änderung gespeichert)</p>
              {items.map((h) => (
                <div key={h.id} data-testid={`course-history-row-${h.id}`} className="flex items-center justify-between gap-3 bg-[#151515] border border-white/8 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-white text-sm">{ACTION_LABEL[h.action] || h.label}</p>
                    <p className="text-[11px] text-neutral-500 flex items-center gap-2 mt-0.5">
                      <CalendarClock size={12} /> {fmtDateTime(h.created_at)} · {h.editor || "—"} · {h.session_count} Termine
                    </p>
                  </div>
                  <button onClick={() => restore(h)} disabled={busy} data-testid={`course-restore-btn-${h.id}`}
                    className="shrink-0 flex items-center gap-1.5 border border-white/20 text-neutral-200 hover:border-[#ff0033] hover:text-[#ff0033] text-xs font-heading uppercase tracking-wider px-3 py-2 transition-colors disabled:opacity-40">
                    <RotateCcw size={13} /> Wiederherstellen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const CourseTrashModal = ({ onClose, onRestored }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/courses-deleted`).then((r) => setItems(r.data.items)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (h) => {
    if (!window.confirm(`Gelöschten Kurs "${h.course_name}" inkl. Termine wiederherstellen?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/course-history/${h.id}/restore`);
      toast.success(`Kurs "${h.course_name}" wiederhergestellt`);
      onRestored && onRestored();
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-[#0F0F0F] border border-white/15 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="course-trash-modal">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 sticky top-0 bg-[#0F0F0F]">
          <div className="flex items-center gap-2"><Trash2 className="text-[#ff0033]" size={18} /><span className="font-heading uppercase tracking-tight text-white">Papierkorb · gelöschte Kurse</span></div>
          <button onClick={onClose} data-testid="course-trash-close" className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5">
          {items === null ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#ff0033]" /></div>
          ) : items.length === 0 ? (
            <p className="text-neutral-500 text-sm text-center py-6">Keine gelöschten Kurse zum Wiederherstellen.</p>
          ) : (
            <div className="space-y-2">
              {items.map((h) => (
                <div key={h.id} data-testid={`course-trash-row-${h.id}`} className="flex items-center justify-between gap-3 bg-[#151515] border border-white/8 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-white text-sm">{h.course_name}</p>
                    <p className="text-[11px] text-neutral-500 flex items-center gap-2 mt-0.5">
                      <CalendarClock size={12} /> gelöscht {fmtDateTime(h.created_at)} · {h.editor || "—"} · {h.session_count} Termine
                    </p>
                  </div>
                  <button onClick={() => restore(h)} disabled={busy} data-testid={`course-trash-restore-${h.id}`}
                    className="shrink-0 flex items-center gap-1.5 bg-[#ff0033] text-white text-xs font-heading uppercase tracking-wider px-3 py-2 hover:bg-[#cc0029] transition-colors disabled:opacity-40">
                    <RotateCcw size={13} /> Wiederherstellen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
